import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import WarningDialogIcon from "@entrilia/shared/icons/WarningDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { ClientConfigurationAssignment } from "./configurationsListState";

const assignClientToConfiguration = withErrorHandling(adminApi.assignClientToDataModelConfiguration);

interface Props {
  assignment: ClientConfigurationAssignment;
  onClose: () => void;
  onAssigned: (assignment: ClientConfigurationAssignment) => void;
}

const AssignClientToConfigurationDialog = ({ assignment, onClose, onAssigned }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [loading, setLoading] = useState(false);

  const handleApplyClick = async () => {
    setLoading(true);
    const [, error] = await assignClientToConfiguration({
      targetConfigurationId: assignment.toConfiguration.id,
      clientCode: assignment.clientCode,
    });
    setLoading(false);

    if (error) {
      logError(error, "[AssignClientToConfigurationDialog]");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Configuration updated successfully");
    onAssigned(assignment);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Change Configuration</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <WarningDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Change configuration for {assignment.clientTitle}?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to change configuration from <strong>{assignment.fromConfiguration.name}</strong> to{" "}
              <strong>{assignment.toConfiguration.name}</strong>. Changing the configuration will affect the display of
              layouts in objects. Custom data on objects may be hidden.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={loading} onClick={handleApplyClick}>
          Apply
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default AssignClientToConfigurationDialog;
